import { CUSTOM_FIELD_TYPES, type CustomFieldType, type ObjectName, OBJECTS } from '@crm/shared';
import { useQueryClient } from '@tanstack/react-query';
import { type FormEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api, ApiError } from '../lib/api';
import { closeAnimated } from '../lib/motion';
import { useSession } from '../lib/session';
import { ObjectIcon } from './ObjectIcon';

// ---------------------------------------------------------------------------
// New custom field: a three-step guided dialog (where → what kind → details)
// ---------------------------------------------------------------------------

const WHERE: { o: ObjectName; text: string }[] = [
  { o: 'opportunity', text: 'Deals with current and potential customers' },
  { o: 'account', text: 'Companies you do business with' },
  { o: 'contact', text: 'People at the companies you do business with' },
];

const KINDS: Record<CustomFieldType, { label: string; text: string }> = {
  text: { label: 'Text', text: 'A short line of text, such as a project code.' },
  textarea: { label: 'Long text', text: 'Several lines, such as notes or requirements.' },
  picklist: { label: 'Picklist', text: 'Choose one value from a list you define.' },
  number: { label: 'Number', text: 'A whole number, such as floors or units.' },
  currency: { label: 'Currency', text: 'An amount of money in your currency.' },
  percent: { label: 'Percent', text: 'A percentage, such as a discount.' },
  date: { label: 'Date', text: 'A calendar date, such as a tender deadline.' },
  boolean: { label: 'Checkbox', text: 'Yes or no, such as “Site visit done”.' },
  email: { label: 'Email', text: 'An email address.' },
  phone: { label: 'Phone', text: 'A phone number.' },
  url: { label: 'Website', text: 'A web address.' },
};

function Steps({ step }: { step: number }) {
  return (
    <ol className="wizard-steps" aria-label={`Step ${step + 1} of 3`}>
      {['Where', 'Type', 'Details'].map((s, i) => (
        <li key={s} className={i < step ? 'done' : i === step ? 'current' : ''}><span aria-hidden="true" />{s}</li>
      ))}
    </ol>
  );
}

export function NewFieldWizard({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const ref = useRef<HTMLDialogElement>(null);
  const [step, setStep] = useState(0);
  const [object, setObject] = useState<ObjectName | null>(null);
  const [type, setType] = useState<CustomFieldType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  useEffect(() => { ref.current?.showModal(); }, []);

  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const label = String(f.get('label') ?? '').trim();
    const options = type === 'picklist'
      ? String(f.get('options') ?? '').split('\n').map((s) => s.trim()).filter(Boolean).map((l) => ({ label: l }))
      : undefined;
    setSaving(true); setError(null);
    try {
      await api(`/api/setup/objects/${object}/custom-fields`, {
        method: 'POST',
        body: { label, type, required: f.get('required') === 'on', listDefault: f.get('listDefault') === 'on', ...(options ? { options } : {}) },
      });
      await qc.invalidateQueries({ queryKey: ['session'] });
      void qc.invalidateQueries({ queryKey: ['setup'] });
      setDone(label);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create the field.');
    } finally { setSaving(false); }
  }

  return (
    <dialog ref={ref} className="modal wizard" onClose={onClose} aria-labelledby="wiz-title">
      <button className="wizard-close" aria-label="Close" onClick={onClose}>×</button>
      <header className="wizard-head">
        <h2 id="wiz-title">New custom field</h2>
        <p className="muted">
          {done ? 'Your field is ready.' : step === 0 ? 'Select where you’d like your new field to appear.' : step === 1 ? `What kind of information will ${object ? OBJECTS[object].pluralLabel.toLowerCase() : ''} hold?` : 'Name your field and choose how it behaves.'}
        </p>
      </header>

      {done ? (
        <div className="wizard-body wizard-done">
          <p><b>{done}</b> now appears on every {object && OBJECTS[object].label.toLowerCase()} record page and in the forms.</p>
          <p className="muted small">To change its position on the page, or who can see it, use Advanced setup → Object manager.</p>
        </div>
      ) : step === 0 ? (
        <div className="wizard-body choices">
          {WHERE.map(({ o, text }) => (
            <button key={o} className={`choice${object === o ? ' selected' : ''}`} onClick={() => { setObject(o); setStep(1); }}>
              <ObjectIcon name={o} />
              <span><b>{OBJECTS[o].pluralLabel}</b><span className="muted">{text}</span></span>
            </button>
          ))}
          <p className="muted small">Your new field will appear on all record pages for the selected object. To limit who sees certain fields, use <Link to="/setup/objects" onClick={onClose}>Advanced setup</Link>.</p>
        </div>
      ) : step === 1 ? (
        <div className="wizard-body choices grid">
          {CUSTOM_FIELD_TYPES.map((t) => (
            <button key={t} className={`choice${type === t ? ' selected' : ''}`} onClick={() => { setType(t); setStep(2); }}>
              <span><b>{KINDS[t].label}</b><span className="muted">{KINDS[t].text}</span></span>
            </button>
          ))}
        </div>
      ) : (
        <form id="wiz-form" className="wizard-body stack" onSubmit={save}>
          <label className="field"><span className="label">Field name</span>
            <input name="label" required maxLength={80} placeholder={type === 'date' ? 'e.g. Tender deadline' : type === 'picklist' ? 'e.g. Project type' : 'e.g. Project code'} autoFocus />
          </label>
          {type === 'picklist' && (
            <label className="field"><span className="label">Values (one per line)</span>
              <textarea name="options" rows={5} required placeholder={'Hotel\nHospital\nMall\nResidential'} />
            </label>
          )}
          <label className="radio"><input type="checkbox" name="required" /> Required: a record can’t be saved without it</label>
          <label className="radio"><input type="checkbox" name="listDefault" /> Show it as a column in lists</label>
          {error && <p className="form-error" role="alert">{error}</p>}
        </form>
      )}

      <footer className="wizard-foot">
        <Steps step={done ? 3 : step} />
        <div className="actions">
          {done ? <button className="btn primary" onClick={onClose}>Done</button> : (
            <>
              {step > 0 && <button className="btn" onClick={() => { setStep(step - 1); setError(null); }}>Back</button>}
              {step === 2 && <button className="btn primary" form="wiz-form" disabled={saving}>{saving ? 'Creating…' : 'Create field'}</button>}
            </>
          )}
        </div>
      </footer>
    </dialog>
  );
}

// ---------------------------------------------------------------------------
// Quick settings: the gear menu's side panel
// ---------------------------------------------------------------------------

function Item({ icon, title, text, to, onClick }: { icon: ReactNode; title: string; text: string; to?: string; onClick: () => void }) {
  const body = <><span className="qs-icon" aria-hidden="true">{icon}</span><span><b>{title}</b><span className="muted">{text}</span></span></>;
  return <li>{to ? <Link className="qs-item" to={to} onClick={onClick}>{body}</Link> : <button className="qs-item" onClick={onClick}>{body}</button>}</li>;
}

const svg = (d: string) => <svg viewBox="0 0 24 24"><path d={d} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>;

export function QuickSettings({ onClose }: { onClose: () => void }) {
  const { permissions } = useSession();
  const navigate = useNavigate();
  const [wizard, setWizard] = useState(false);
  const canSetup = permissions.system.manageSetup;
  const canUsers = permissions.system.manageUsers;
  // The panel slides out before it closes (not when going to another page).
  const backdrop = useRef<HTMLDivElement>(null);
  const leave = () => closeAnimated(backdrop.current, onClose);
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape' && !wizard) closeAnimated(backdrop.current, onClose); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onClose, wizard]);

  if (wizard) return <NewFieldWizard onClose={() => { setWizard(false); onClose(); }} />;
  return (
    <div ref={backdrop} className="qs-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) leave(); }}>
      <aside className="qs-panel" role="dialog" aria-modal="true" aria-labelledby="qs-title">
        <header>
          <h2 id="qs-title">Quick settings</h2>
          <button className="icon" aria-label="Close" onClick={leave}>×</button>
        </header>
        <button className="btn qs-advanced" onClick={() => { onClose(); navigate('/setup/home'); }}>
          Open advanced setup
          <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M11 4h5v5M16 4l-7 7M14 12v4H4V6h4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        {canSetup && (
          <section>
            <h3>Customization</h3>
            <ul>
              <Item icon={svg('M6 4h9l3 3v13H6zM9 11h6M9 15h4')} title="Fields" text="Create a custom input field." onClick={() => setWizard(true)} />
              <Item icon={svg('M4 7l4 5-4 5M11 7l4 5-4 5M18 7v10')} title="Sales stages" text="Customize stages to match your process." to="/setup/stages" onClick={onClose} />
              <Item icon={svg('M5 5h6v6H5zM13 5h6v6h-6zM5 13h6v6H5zM13 13h6v6h-6z')} title="Page layouts" text="Arrange fields on record pages." to="/setup/objects" onClick={onClose} />
            </ul>
          </section>
        )}
        <section>
          <h3>Company</h3>
          <ul>
            {canUsers && <Item icon={svg('M10 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7ZM3.5 20c.6-3.5 3.2-5.5 6.5-5.5 1.4 0 2.6.3 3.6.9M18 14v6M15 17h6')} title="Users" text="Add and manage users." to="/setup/users" onClick={onClose} />}
            {canSetup && <Item icon={svg('M4 20V8l8-4 8 4v12M9 20v-5h6v5M8 11h.01M12 11h.01M16 11h.01')} title="Business details" text="Company name, CR and VAT numbers, contact details." to="/setup/business" onClick={onClose} />}
            {canSetup && <Item icon={svg('M12 3.5 19 6v5.5c0 4.3-3 7.6-7 9-4-1.4-7-4.7-7-9V6l7-2.5ZM9 12l2 2 4-4')} title="Security" text="Two-step sign-in, sessions and office IP ranges." to="/setup/security" onClick={onClose} />}
            <Item icon={svg('M12 4v11M7.5 10.5 12 15l4.5-4.5M5 19h14')} title="Import data" text="Bring in companies, contacts and deals from CSV." to="/import" onClick={onClose} />
          </ul>
        </section>
      </aside>
    </div>
  );
}
